export const DISCIPLINE_FALLBACK_IMAGE = "/img/no-image.jpg";

export function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

export function toNumber(value, defaultValue = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : defaultValue;
}

export function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function stripHtml(value = "") {
  return String(value).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export function sortDisciplinesByOrder(rows) {
  return safeArray(rows)
    .slice()
    .sort((a, b) => {
      const orderA = toNumber(a?.ordre_discipline, 0);
      const orderB = toNumber(b?.ordre_discipline, 0);

      if (orderA !== orderB) return orderA - orderB;

      return String(a?.nom_discipline || "").localeCompare(
        String(b?.nom_discipline || ""),
        "fr"
      );
    });
}

export function normalizeOrderDisciplines(rows) {
  return sortDisciplinesByOrder(rows).map((discipline, index) => ({
    ...discipline,
    ordre_discipline: index + 1,
  }));
}

export function filterDisciplines(disciplines, filters) {
  const query = normalizeText(filters?.query || "");

  return sortDisciplinesByOrder(disciplines).filter((discipline) => {
    if (!query) return true;

    const searchable = normalizeText(
      `${discipline?.nom_discipline || ""} ${
        discipline?.description_discipline || ""
      } ${stripHtml(discipline?.contenu_discipline || "")}`
    );

    return searchable.includes(query);
  });
}

export function calculateDisciplinesStats(disciplines) {
  const rows = safeArray(disciplines);

  let withImage = 0;
  let withoutImage = 0;
  let withDescription = 0;
  let withContent = 0;

  for (const discipline of rows) {
    if (String(discipline?.image_discipline || "").trim()) withImage += 1;
    else withoutImage += 1;

    if (String(discipline?.description_discipline || "").trim()) {
      withDescription += 1;
    }

    if (stripHtml(discipline?.contenu_discipline || "")) {
      withContent += 1;
    }
  }

  return {
    total: rows.length,
    withImage,
    withoutImage,
    withDescription,
    withContent,
  };
}

export function getNextDisciplineOrder(disciplines) {
  return safeArray(disciplines).reduce((max, discipline) => {
    return Math.max(max, toNumber(discipline?.ordre_discipline, 0));
  }, 0) + 1;
}

export function getDefaultDisciplineForm(order = 1) {
  return {
    id: "",
    nom_discipline: "",
    description_discipline: "",
    image_discipline: "",
    contenu_discipline: "",
    ordre_discipline: order,
  };
}

export function mapDisciplineToForm(discipline, order = 1) {
  if (!discipline) return getDefaultDisciplineForm(order);

  return {
    id: discipline?.id || "",
    nom_discipline: discipline?.nom_discipline || "",
    description_discipline: discipline?.description_discipline || "",
    image_discipline: discipline?.image_discipline || "",
    contenu_discipline: discipline?.contenu_discipline || "",
    ordre_discipline: discipline?.ordre_discipline ?? order,
  };
}

export function buildDisciplinePayload(formData) {
  return {
    nom_discipline: String(formData?.nom_discipline || "").trim(),
    description_discipline: String(
      formData?.description_discipline || ""
    ).trim(),
    image_discipline: String(formData?.image_discipline || "").trim(),
    contenu_discipline: String(formData?.contenu_discipline || ""),
    ordre_discipline: toNumber(formData?.ordre_discipline, 0),
  };
}

export function validateDisciplinePayload(payload) {
  if (!payload.nom_discipline) return "Le nom de la discipline est requis.";

  if (!payload.description_discipline) {
    return "La description de la discipline est requise.";
  }

  if (!Number.isFinite(Number(payload.ordre_discipline))) {
    return "L’ordre d’affichage doit être un nombre.";
  }

  return "";
}

export function validateImageFile(file) {
  if (!file) return "";

  const validType = /^image\/(png|jpe?g|webp)$/i.test(file.type);

  if (!validType) {
    return "Format image non supporté. Utilisez PNG, JPG ou WEBP.";
  }

  if (file.size > 10 * 1024 * 1024) {
    return "Image trop volumineuse. Taille maximale : 10 Mo.";
  }

  return "";
}

export function extractCreatedDisciplineId(response) {
  const data = response?.data ?? response;

  return (
    data?.id ||
    data?.insertId ||
    data?.discipline?.id ||
    data?.data?.id ||
    data?.data?.insertId ||
    data?.payload?.id ||
    (Array.isArray(data) ? data[0]?.id : null) ||
    null
  );
}

export function canCropDisciplineImage(imageUrl) {
  return String(imageUrl || "").startsWith("/uploads/");
}